import { useEffect, useState, useCallback, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { InfoCard } from "@/components/toggles";
import { useAuth } from "@/contexts/AuthContext";
import { getPresale, buyerCounterRespond, sellerDecide, sellerWithdraw, listPresaleMessages, sendPresaleMessage, markNotificationsRead, parseInquiryError, REASON_CODE_LABELS } from "@/lib/modulD";
import { ArrowLeft, ShoppingBag, Check, X, Edit3, AlertCircle, Clock, User, MessageSquare, Send, Flag } from "lucide-react";
import MessageReportDialog from "@/components/MessageReportDialog";

const STATUS_LABEL = {
  DRAFT: "Entwurf", SUBMITTED: "Eingereicht",
  SELLER_ACCEPTED: "Akzeptiert", SELLER_MODIFIED: "Gegenangebot offen", SELLER_REJECTED: "Abgelehnt",
  BUYER_COUNTER_ACCEPTED: "Buyer hat angenommen", BUYER_COUNTER_DECLINED: "Buyer hat abgelehnt",
  TEMP_LISTING_CREATED: "Privates Angebot erstellt",
  CLOSED: "Beendet", CLOSED_ITEM_UNAVAILABLE: "Artikel nicht mehr verfügbar",
};
const STATUS_COLOR = {
  SUBMITTED: "text-amber-700 bg-amber-50 border-amber-200",
  SELLER_ACCEPTED: "text-green-700 bg-green-50 border-green-200",
  SELLER_MODIFIED: "text-blue-700 bg-blue-50 border-blue-200",
  SELLER_REJECTED: "text-red-600 bg-red-50 border-red-200",
  TEMP_LISTING_CREATED: "text-green-800 bg-green-100 border-green-300",
  CLOSED: "text-[#999] bg-[#FAFAFA] border-[#E5E5E5]",
  CLOSED_ITEM_UNAVAILABLE: "text-[#999] bg-[#FAFAFA] border-[#E5E5E5]",
};

export default function PresaleDetailPage() {
  const { t } = useTranslation();
  const { presaleId } = useParams();
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();
  const [presale, setPresale] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [working, setWorking] = useState(false);
  const [modify, setModify] = useState(false);
  const [modPrice, setModPrice] = useState("");
  const [modMessage, setModMessage] = useState("");
  // Free-text messages (4c.bug11)
  const [textMessages, setTextMessages] = useState([]);
  const [msgText, setMsgText] = useState("");
  const [msgSending, setMsgSending] = useState(false);
  const [msgError, setMsgError] = useState(null);
  const [msgWarning, setMsgWarning] = useState(null);
  // Sub-Batch 4c.5: Report dialog state
  const [reportingMessage, setReportingMessage] = useState(null);
  const [reportedIds, setReportedIds] = useState(new Set());
  const msgListRef = useRef(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const d = await getPresale(presaleId);
      setPresale(d);
      setError(null);
      // also load text messages
      try {
        const m = await listPresaleMessages(presaleId);
        setTextMessages(m?.messages || []);
      } catch {
        setTextMessages([]);
      }
    } catch (e) {
      const detail = e?.response?.data?.detail;
      setError(typeof detail === "string" ? detail : "Konnte Anfrage nicht laden");
    } finally {
      setLoading(false);
    }
  }, [presaleId]);

  useEffect(() => {
    if (authLoading) return;
    if (!user) { navigate("/login"); return; }
    load();
    // Mark all notifications for this presale as read on open
    markNotificationsRead({ presale_id: presaleId }).catch(() => {});
  }, [user, authLoading, presaleId, load, navigate]);

  if (loading) return <div className="flex justify-center py-16"><div className="w-6 h-6 border-2 border-[#E5E5E5] border-t-[#0A0A0A] rounded-full animate-spin" /></div>;
  if (error || !presale) {
    return (
      <div className="px-6 py-16 text-center">
        <AlertCircle size={28} className="mx-auto text-amber-500 mb-3" />
        <p className="text-sm text-[#666]">{error || "Anfrage nicht gefunden"}</p>
        <button onClick={() => navigate("/messages")} className="mt-4 text-xs text-[#0A0A0A] underline">{t("presale.back")}</button>
      </div>
    );
  }

  const isBuyer = user._id === presale.buyer_id;
  const isSeller = user._id === presale.seller_id;
  const status = presale.status;
  const isCounterPending = status === "SELLER_MODIFIED" && presale.buyer_counter_response_status === "PENDING";
  const tempListing = presale.temp_listing;

  const handleBuyerCounter = async (decision) => {
    setWorking(true);
    try {
      await buyerCounterRespond(presaleId, decision);
      await load();
    } catch (e) {
      alert(e?.response?.data?.detail || "Fehler");
    } finally { setWorking(false); }
  };

  const handleSellerDecide = async (decision) => {
    setWorking(true);
    try {
      const body = { decision };
      if (decision === "MODIFY") {
        const price = parseFloat(modPrice);
        if (!price || price <= 0) { alert(t("presale.enter_valid_price")); setWorking(false); return; }
        body.counter_price = price;
        body.message = modMessage || null;
      }
      await sellerDecide(presaleId, body);
      setModify(false); setModPrice(""); setModMessage("");
      await load();
    } catch (e) {
      const parsed = parseInquiryError(e);
      if (parsed.blocked) alert(`${REASON_CODE_LABELS[parsed.reason_code] || "Blockiert"}\n\n${parsed.reason_text}`);
      else alert(parsed.message || "Fehler");
    } finally { setWorking(false); }
  };

  const handleWithdraw = async () => {
    if (!window.confirm(t("presale.confirm_withdraw"))) return;
    setWorking(true);
    try { await sellerWithdraw(presaleId); await load(); }
    catch (e) { alert(e?.response?.data?.detail || "Fehler"); }
    finally { setWorking(false); }
  };

  const handleSendMessage = async () => {
    const t = msgText.trim();
    if (!t || msgSending) return;
    setMsgSending(true);
    setMsgError(null);
    setMsgWarning(null);
    try {
      const res = await sendPresaleMessage(presaleId, t);
      setMsgText("");
      // Append the new message (using the response data)
      if (res?.message) {
        setTextMessages((prev) => [...prev, res.message]);
        // If redacted, show explicit notice to sender
        if (res.message.redaction_count > 0 || res.policy?.decision === "REDACTED") {
          setMsgWarning(
            `⚠ Hinweis: Deine Nachricht enthielt ${res.message.redaction_count || "Kontakt-"}` +
            `${res.message.redaction_count === 1 ? "Kontaktdatum" : "Kontaktdaten"} ` +
            `(z.B. Telefonnummer, E-Mail, Social-Handle, Adresse). ` +
            `Diese wurden automatisch entfernt. Die Weitergabe persönlicher Daten ist auf stilltouched nicht zulässig.`
          );
        }
      } else {
        const m = await listPresaleMessages(presaleId);
        setTextMessages(m?.messages || []);
      }
      setTimeout(() => msgListRef.current?.scrollIntoView({ behavior: "smooth", block: "end" }), 50);
    } catch (e) {
      const parsed = parseInquiryError(e);
      if (parsed.blocked) {
        setMsgError(`${REASON_CODE_LABELS[parsed.reason_code] || "Blockiert"}: ${parsed.reason_text}`);
      } else {
        setMsgError(parsed.message || "Nachricht konnte nicht gesendet werden.");
      }
    } finally {
      setMsgSending(false);
    }
  };

  const MESSAGES_BLOCKED_STATUSES = ["CLOSED", "CLOSED_ITEM_UNAVAILABLE", "BUYER_COUNTER_DECLINED", "SELLER_REJECTED"];

  const buyerName = presale.buyer_alias || "Buyer";
  const sellerName = presale.seller?.display_name || presale.seller?.handle || "Seller";
  const fmt = (ts) => ts ? new Date(ts).toLocaleString("de-DE", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" }) : "";

  return (
    <div data-testid="presale-detail-page" className="bg-white min-h-screen pb-20">
      {/* Header */}
      <div className="sticky top-0 z-10 bg-white border-b border-[#E5E5E5] px-4 py-3 flex items-center gap-3">
        <button data-testid="back-btn" onClick={() => navigate(-1)} className="p-1 -ml-1"><ArrowLeft size={20} /></button>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-bold text-[#0A0A0A]">{t("presale.title")}</p>
          <p className="text-[10px] text-[#999]">{isSeller ? `von ${buyerName}` : `an ${sellerName}`}</p>
        </div>
        <span className={`text-[10px] px-2 py-0.5 border ${STATUS_COLOR[status] || "text-[#999] border-[#E5E5E5]"}`}>
          {STATUS_LABEL[status] || status}
        </span>
      </div>

      {/* Listing Reference */}
      {presale.listing && presale.listing_id && (
        <button data-testid="listing-card"
          onClick={() => navigate(`/stilltouchedx/${presale.listing_id}`)}
          className="w-full flex items-center gap-3 px-4 py-3 border-b border-[#E5E5E5] bg-[#FAFAFA] hover:bg-[#F0F0F0] text-left">
          <div className="w-12 h-14 bg-[#E5E5E5] shrink-0 overflow-hidden">
            {presale.listing.media_urls?.[0] && <img src={presale.listing.media_urls[0]} alt="" className="w-full h-full object-cover" />}
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-[10px] text-[#999]">{t("presale.article_reference")}</p>
            <p className="text-sm font-medium text-[#0A0A0A] truncate">{presale.listing.title || "Artikel"}</p>
            {presale.listing.price !== undefined && <p className="text-xs text-[#666] mt-0.5">{Number(presale.listing.price).toFixed(2)} EUR</p>}
          </div>
          <span className="text-[10px] text-[#999]">›</span>
        </button>
      )}

      {/* Timeline */}
      <div className="px-4 py-5 space-y-4">
        <p className="text-[10px] font-bold text-[#999] uppercase tracking-wider">Verlauf</p>

        {/* Step 1: Buyer Anfrage */}
        <div className="flex gap-3">
          <div className="w-7 h-7 rounded-full bg-[#0A0A0A] text-white text-[10px] flex items-center justify-center shrink-0 font-bold">1</div>
          <div className="flex-1 min-w-0">
            <p className="text-xs font-bold text-[#0A0A0A]">Anfrage von {buyerName}</p>
            <p className="text-[10px] text-[#999] mb-2">{fmt(presale.submitted_at || presale.created_at)}</p>
            <div className="bg-[#FAFAFA] border border-[#E5E5E5] p-3">
              <p className="text-sm text-[#0A0A0A] whitespace-pre-wrap leading-relaxed">{presale.item_description}</p>
              {presale.preferences && <p className="text-xs text-[#666] mt-2"><strong>Wunsch:</strong> {presale.preferences}</p>}
              {presale.budget && <p className="text-xs text-[#666] mt-1"><strong>Budget:</strong> {Number(presale.budget).toFixed(2)} EUR</p>}
              {presale.moderation?.redaction_count > 0 && (
                <p className="text-[10px] text-amber-600 italic mt-2">⚠ {presale.moderation.redaction_count} Kontaktdaten wurden automatisch entfernt</p>
              )}
            </div>
            {/* Melden-Button für den Empfänger (Seller) der initialen Anfrage – REQ-COM-REPORT-001 / PRESALE_INITIAL */}
            {isSeller && (
              <button
                data-testid={`report-btn-initial-${presaleId}`}
                onClick={() => setReportingMessage({
                  message_id: presaleId,
                  message_kind: "PRESALE_INITIAL",
                  content_display: presale.item_description,
                })}
                disabled={reportedIds.has(`PRESALE_INITIAL:${presaleId}`)}
                className={`mt-1 text-[10px] flex items-center gap-0.5 ${
                  reportedIds.has(`PRESALE_INITIAL:${presaleId}`)
                    ? "text-[#CCC] cursor-default"
                    : "text-[#999] hover:text-red-600"
                }`}
                title={reportedIds.has(`PRESALE_INITIAL:${presaleId}`) ? "Bereits gemeldet" : "Diese Anfrage melden"}
              >
                <Flag size={9} />
                {reportedIds.has(`PRESALE_INITIAL:${presaleId}`) ? "gemeldet" : "Anfrage melden"}
              </button>
            )}
          </div>
        </div>

        {/* Step 2: Seller Decision */}
        {presale.seller_decision && (
          <div className="flex gap-3">
            <div className="w-7 h-7 rounded-full bg-[#0A0A0A] text-white text-[10px] flex items-center justify-center shrink-0 font-bold">2</div>
            <div className="flex-1 min-w-0">
              <p className="text-xs font-bold text-[#0A0A0A]">Antwort von {sellerName}</p>
              <p className="text-[10px] text-[#999] mb-2">{fmt(presale.decided_at)}</p>
              {presale.seller_decision === "ACCEPT" && (
                <InfoCard variant="success">
                  <p className="text-sm text-green-700 font-medium">{t("presale.accepted")}</p>
                  <p className="text-xs text-green-700 mt-1">{t("presale.private_created")}</p>
                </InfoCard>
              )}
              {presale.seller_decision === "MODIFY" && presale.counter_offer && (
                <InfoCard variant="info">
                  <p className="text-[10px] font-bold uppercase text-blue-700 mb-1">Gegenangebot</p>
                  <p className="text-2xl font-bold text-blue-900">{Number(presale.counter_offer.price).toFixed(2)} EUR</p>
                  {presale.counter_offer.message && (
                    <p className="text-xs text-blue-800 mt-2 italic">&ldquo;{presale.counter_offer.message}&rdquo;</p>
                  )}
                </InfoCard>
              )}
              {presale.seller_decision === "REJECT" && (
                <InfoCard variant="danger">
                  <p className="text-sm text-red-700 font-medium">{t("presale.declined")}</p>
                  {presale.seller_message && <p className="text-xs text-red-700 mt-1">{presale.seller_message}</p>}
                </InfoCard>
              )}
            </div>
          </div>
        )}

        {/* Step 3: Buyer Counter Response */}
        {presale.buyer_counter_response_status && presale.buyer_counter_response_status !== "NOT_REQUIRED" && (
          <div className="flex gap-3">
            <div className="w-7 h-7 rounded-full bg-[#0A0A0A] text-white text-[10px] flex items-center justify-center shrink-0 font-bold">3</div>
            <div className="flex-1 min-w-0">
              <p className="text-xs font-bold text-[#0A0A0A]">Reaktion von {buyerName}</p>
              <p className="text-[10px] text-[#999] mb-2">{fmt(presale.buyer_decided_at) || "ausstehend"}</p>
              {presale.buyer_counter_response_status === "PENDING" && (
                <InfoCard variant="warning">
                  <p className="text-sm text-amber-700">{t("presale.waiting_buyer")}</p>
                </InfoCard>
              )}
              {presale.buyer_counter_response_status === "ACCEPTED" && (
                <InfoCard variant="success">
                  <p className="text-sm text-green-700 font-medium">✓ Gegenangebot angenommen</p>
                </InfoCard>
              )}
              {presale.buyer_counter_response_status === "DECLINED" && (
                <InfoCard variant="danger">
                  <p className="text-sm text-red-700 font-medium">✗ Gegenangebot abgelehnt</p>
                </InfoCard>
              )}
            </div>
          </div>
        )}

        {/* Step 4: Temp Listing / Closed */}
        {status === "TEMP_LISTING_CREATED" && tempListing && (
          <div className="flex gap-3">
            <div className="w-7 h-7 rounded-full bg-green-600 text-white text-[10px] flex items-center justify-center shrink-0 font-bold">✓</div>
            <div className="flex-1 min-w-0">
              <p className="text-xs font-bold text-[#0A0A0A]">{t("presale.private_offer_created")}</p>
              <InfoCard variant="success" className="mt-2">
                <p className="text-2xl font-bold text-green-900">{Number(tempListing.price || 0).toFixed(2)} EUR</p>
                <p className="text-xs text-green-700 mt-1">{t("presale.private_visible_you")}</p>
                {isBuyer && tempListing.listing_id && (
                  <button data-testid="buy-temp-listing"
                    onClick={() => navigate(`/checkout?listing=${tempListing.listing_id}&area=STILLTOUCHEDX&private=1&presale_id=${presaleId}`)}
                    className="mt-3 w-full h-9 bg-[#0A0A0A] text-white text-xs font-bold flex items-center justify-center gap-1.5 hover:bg-[#222]">
                    <ShoppingBag size={12} /> Jetzt kaufen
                  </button>
                )}
                {isSeller && (
                  <button onClick={handleWithdraw} disabled={working}
                    className="mt-3 text-[10px] text-red-600 underline disabled:opacity-40">{t("presale.withdraw")}</button>
                )}
              </InfoCard>
            </div>
          </div>
        )}
      </div>

      {/* Free-Text Messages Section (4c.bug11) */}
      {(isBuyer || isSeller) && (
        <div data-testid="presale-messages-section" className="border-t border-[#E5E5E5] mt-2">
          <div className="px-4 py-3 bg-[#FAFAFA] border-b border-[#E5E5E5] flex items-center gap-2">
            <MessageSquare size={14} className="text-[#666]" />
            <p className="text-xs font-bold text-[#0A0A0A] uppercase tracking-wider">{t("presale.messages")}</p>
            {textMessages.length > 0 && (
              <span className="text-[10px] text-[#999]">({textMessages.length})</span>
            )}
          </div>

          {/* Messages List */}
          <div className="px-4 py-3 space-y-2 max-h-80 overflow-y-auto">
            {textMessages.length === 0 ? (
              <p className="text-[11px] text-[#999] italic text-center py-2">
                Noch keine Nachrichten. Schreibe eine Antwort oder Rückfrage.
              </p>
            ) : (
              textMessages.map((m) => {
                const mine = m.sender_id === user._id;
                const canReport = !mine;
                const alreadyReported = reportedIds.has(m.message_id);
                return (
                  <div key={m.message_id} className={`flex ${mine ? "justify-end" : "justify-start"}`}>
                    <div className={`max-w-[80%] flex flex-col ${mine ? "items-end" : "items-start"}`}>
                      <div className={`px-3 py-2 text-sm ${
                        mine
                          ? "bg-[#0A0A0A] text-white"
                          : "bg-[#F0F0F0] text-[#0A0A0A] border border-[#E5E5E5]"
                      }`}>
                        <p className="text-[9px] uppercase tracking-wider opacity-70 mb-1">
                          {m.sender_role === "SELLER"
                            ? (mine ? "Du (Verkäufer*in)" : sellerName)
                            : (mine ? "Du (Käufer*in)" : buyerName)}
                          {" · "}
                          {fmt(m.created_at)}
                        </p>
                        <p className="whitespace-pre-wrap leading-relaxed">{m.content_display}</p>
                        {m.redaction_count > 0 && (
                          <p className="text-[10px] mt-1 italic opacity-70">
                            ⚠ {m.redaction_count} Kontaktdaten automatisch entfernt
                          </p>
                        )}
                      </div>
                      {canReport && (
                        <button
                          data-testid={`report-btn-${m.message_id}`}
                          onClick={() => setReportingMessage(m)}
                          disabled={alreadyReported}
                          className={`mt-0.5 text-[10px] flex items-center gap-0.5 ${
                            alreadyReported ? "text-[#CCC] cursor-default" : "text-[#999] hover:text-red-600"
                          }`}
                          title={alreadyReported ? "Bereits gemeldet" : "Diese Nachricht melden"}
                        >
                          <Flag size={9} />
                          {alreadyReported ? "gemeldet" : "melden"}
                        </button>
                      )}
                    </div>
                  </div>
                );
              })
            )}
            <div ref={msgListRef} />
          </div>

          {/* Input or Status Hint */}
          {MESSAGES_BLOCKED_STATUSES.includes(status) ? (
            <div className="px-4 py-3 border-t border-[#E5E5E5] bg-[#FAFAFA]">
              <p className="text-[11px] text-[#999] text-center italic">
                In diesem Status sind keine Nachrichten mehr möglich.
              </p>
            </div>
          ) : (
            <div className="px-4 py-3 border-t border-[#E5E5E5] bg-white">
              {msgError && (
                <InfoCard variant="danger" className="mb-2">
                  <p className="text-[11px] text-red-700">{msgError}</p>
                </InfoCard>
              )}
              {msgWarning && (
                <InfoCard variant="warning" className="mb-2">
                  <p className="text-[11px] text-amber-900 leading-relaxed">{msgWarning}</p>
                  <button
                    onClick={() => setMsgWarning(null)}
                    className="mt-1 text-[10px] text-amber-700 underline hover:text-amber-900"
                  >
                    Verstanden
                  </button>
                </InfoCard>
              )}
              <div className="flex gap-2">
                <input
                  data-testid="presale-msg-input"
                  type="text"
                  value={msgText}
                  onChange={(e) => setMsgText(e.target.value)}
                  onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); handleSendMessage(); } }}
                  disabled={msgSending}
                  placeholder={t("presale.reply_placeholder")}
                  className="flex-1 h-10 px-3 border border-[#E5E5E5] text-sm focus:outline-none focus:ring-1 focus:ring-[#0A0A0A] disabled:opacity-50"
                  maxLength={2000}
                />
                <button
                  data-testid="presale-msg-send"
                  onClick={handleSendMessage}
                  disabled={msgSending || !msgText.trim()}
                  className="rounded-full h-10 px-3 bg-[#0A0A0A] text-white flex items-center justify-center disabled:opacity-40"
                  aria-label={t("presale.send_msg_aria")}
                >
                  {msgSending ? <Clock size={14} className="animate-spin" /> : <Send size={14} />}
                </button>
              </div>
              <p className="text-[10px] text-[#999] mt-1">
                Nachrichten werden auf Kontaktdaten und Policy-Verstöße geprüft.
              </p>
            </div>
          )}
        </div>
      )}

      {/* Buyer Actions */}
      {isCounterPending && isBuyer && (
        <div data-testid="buyer-counter-actions" className="fixed bottom-16 left-0 right-0 max-w-md mx-auto bg-white border-t border-[#E5E5E5] p-3 flex gap-2 z-30">
          <button data-testid="buyer-decline" onClick={() => handleBuyerCounter("DECLINE")} disabled={working}
            className="flex-1 h-11 border border-red-300 text-red-600 text-sm font-bold flex items-center justify-center gap-1 disabled:opacity-40">
            <X size={14} /> Ablehnen
          </button>
          <button data-testid="buyer-accept" onClick={() => handleBuyerCounter("ACCEPT")} disabled={working}
            className="flex-1 h-11 bg-green-600 text-white text-sm font-bold flex items-center justify-center gap-1 disabled:opacity-40">
            <Check size={14} /> Annehmen
          </button>
        </div>
      )}

      {/* Seller Actions */}
      {status === "SUBMITTED" && isSeller && (
        <div data-testid="seller-actions" className="fixed bottom-16 left-0 right-0 max-w-md mx-auto bg-white border-t border-[#E5E5E5] p-3 z-30">
          {!modify ? (
            <div className="flex gap-2">
              <button data-testid="seller-accept" onClick={() => handleSellerDecide("ACCEPT")} disabled={working}
                className="flex-1 h-10 bg-green-600 text-white text-xs font-bold flex items-center justify-center gap-1 disabled:opacity-40">
                <Check size={12} /> Annehmen
              </button>
              <button data-testid="seller-modify" onClick={() => setModify(true)} disabled={working || (presale.counter_round_count || 0) >= 1}
                className="flex-1 rounded-full h-10 bg-[#0A0A0A] text-white text-xs font-bold flex items-center justify-center gap-1 disabled:opacity-40">
                <Edit3 size={12} /> Gegenangebot
              </button>
              <button data-testid="seller-reject" onClick={() => handleSellerDecide("REJECT")} disabled={working}
                className="flex-1 h-10 border border-red-300 text-red-600 text-xs font-bold flex items-center justify-center gap-1 disabled:opacity-40">
                <X size={12} /> Ablehnen
              </button>
            </div>
          ) : (
            <div className="space-y-2">
              <input data-testid="mod-price" type="number" step="0.01" min="0.01" value={modPrice} onChange={(e) => setModPrice(e.target.value)}
                className="w-full rounded-full h-9 px-3 border border-[#E5E5E5] text-sm focus:outline-none focus:ring-1 focus:ring-[#0A0A0A]" placeholder={`Neuer Preis (EUR) ${presale.budget ? `– Budget Buyer: ${presale.budget}` : ""}`} />
              <input data-testid="mod-message" value={modMessage} onChange={(e) => setModMessage(e.target.value)}
                className="w-full rounded-full h-9 px-3 border border-[#E5E5E5] text-sm focus:outline-none focus:ring-1 focus:ring-[#0A0A0A]" placeholder={t("presale.msg_policy_placeholder")} />
              <div className="flex gap-2">
                <button onClick={() => { setModify(false); setModPrice(""); setModMessage(""); }} disabled={working} className="rounded-full h-9 px-3 border border-[#E5E5E5] text-xs">{t("presale.cancel")}</button>
                <button data-testid="mod-submit" onClick={() => handleSellerDecide("MODIFY")} disabled={working || !modPrice}
                  className="flex-1 h-9 bg-[#0A0A0A] text-white text-xs font-bold disabled:opacity-40">{working ? "..." : "Gegenangebot senden"}</button>
              </div>
              <p className="text-[10px] text-[#999]">{t("presale.max_one_counter")}</p>
            </div>
          )}
        </div>
      )}

      {/* Buyer waiting state (status SUBMITTED, no seller response yet) */}
      {status === "SUBMITTED" && isBuyer && (
        <InfoCard variant="warning" testId="buyer-waiting" className="mx-4 mb-6 text-center">
          <Clock size={20} className="mx-auto text-amber-600 mb-2" />
          <p className="text-sm font-medium text-amber-700">{t("presale.waiting_seller")}</p>
          <p className="text-xs text-amber-600 mt-1">{t("presale.waiting_seller_body")}</p>
        </InfoCard>
      )}

      {/* Hinweis wenn weder Buyer noch Seller (sollte nicht passieren – Auth-Filter) */}
      {!isBuyer && !isSeller && (
        <InfoCard variant="danger" className="mx-4 mb-6 text-center">
          <AlertCircle size={20} className="mx-auto text-red-600 mb-2" />
          <p className="text-sm font-medium text-red-700">{t("presale.not_participant")}</p>
        </InfoCard>
      )}

      {/* Message-Report-Dialog (Sub-Batch 4c.5) */}
      <MessageReportDialog
        open={!!reportingMessage}
        onClose={() => setReportingMessage(null)}
        messageKind={reportingMessage?.message_kind || "PRESALE_MSG"}
        messageId={reportingMessage?.message_id}
        messagePreview={reportingMessage?.content_display}
        onReported={() => {
          if (reportingMessage?.message_id) {
            const key = `${reportingMessage.message_kind || "PRESALE_MSG"}:${reportingMessage.message_id}`;
            setReportedIds((prev) => new Set([...prev, reportingMessage.message_id, key]));
          }
        }}
      />
    </div>
  );
}
